import { useEffect, useState } from 'react';
import { fetchProjects } from '../api/productsApi';
import AileronCta from '../components/sections/AileronCta';
import projects from '../data/projects';

function ProjectThumbnail({ item }) {
  if (item.image) {
    return (
      <img
        src={item.image}
        alt={item.title}
        width="320"
        height="180"
        loading="lazy"
        decoding="async"
        className="h-full w-full rounded-lg bg-white object-contain"
      />
    );
  }

  return (
    <div className="flex h-full w-full flex-col justify-between rounded-lg bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-800 p-3 text-white">
      <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-indigo-200">
        {item.category}
      </span>
      <div>
        <p className="text-base font-semibold leading-tight">{item.title}</p>
        <p className="mt-1 text-xs text-slate-200">{item.status}</p>
      </div>
    </div>
  );
}

function ProjectDetails({ item }) {
  if (!item) {
    return null;
  }

  if (item.type === 'initiative') {
    return (
      <>
        <p className="mt-3 text-sm text-gray-500">
          <span className="font-semibold text-gray-700">Category:</span> {item.category}
        </p>
        <p className="mt-1 text-sm text-gray-500">
          <span className="font-semibold text-gray-700">Audience:</span> {item.audience}
        </p>
        <p className="mt-1 text-sm text-gray-500">
          <span className="font-semibold text-gray-700">Focus:</span> {item.focus}
        </p>
        <p className="mt-1 text-sm text-gray-500">
          <span className="font-semibold text-gray-700">Mission:</span> {item.mission}
        </p>
      </>
    );
  }

  return (
    <>
      <p className="mt-3 text-sm text-gray-500">
        <span className="font-semibold text-gray-700">Category:</span> {item.category}
      </p>
      <p className="mt-1 text-sm text-gray-500">
        <span className="font-semibold text-gray-700">Industry:</span> {item.industry}
      </p>
      <p className="mt-1 text-sm text-gray-500">
        <span className="font-semibold text-gray-700">Problem solved:</span> {item.problemSolved}
      </p>
      <p className="mt-1 text-sm text-gray-500">
        <span className="font-semibold text-gray-700">Stack:</span> {(item.stack || []).join(', ')}
      </p>
    </>
  );
}

export default function ProjectsPage() {
  const [items, setItems] = useState(projects);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const data = await fetchProjects();
        if (mounted) {
          setItems(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (mounted) {
          setError(String(err?.message || err));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedItem) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedItem(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedItem]);

  return (
    <section className="mx-auto max-w-7xl px-6 py-10">
      <h1 className="text-3xl font-bold text-gray-900">Projects & Initiatives</h1>
      <p className="mt-2 max-w-3xl text-gray-600">
        A selection of products and automation initiatives aligned to operational efficiency, reliability, and measurable business outcomes.
      </p>

      {loading && <p className="mt-6 text-gray-600">Loading projects...</p>}
      {error && <p className="mt-6 text-red-600">{error}</p>}

      <div className="mt-8 grid justify-items-center gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setSelectedItem(item)}
            className="w-full max-w-[320px] overflow-hidden rounded-2xl border border-gray-200 bg-white text-left shadow-sm transition hover:-translate-y-1 hover:shadow-md"
          >
            <div className="mx-4 mt-4 aspect-[16/9] w-[calc(100%-2rem)] max-h-40 bg-slate-100">
              <ProjectThumbnail item={item} />
            </div>
            <div className="p-4 pt-3">
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-base font-semibold text-gray-900">{item.title}</h2>
                <span className="rounded-md bg-gray-100 px-2 py-1 text-[11px] font-medium text-gray-700">
                  {item.status}
                </span>
              </div>
              <p className="mt-2 line-clamp-2 text-sm text-gray-600">{item.shortDescription}</p>
              <p className="mt-3 text-xs font-medium uppercase tracking-wide text-indigo-600">
                View details
              </p>
            </div>
          </button>
        ))}
      </div>

      {selectedItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4 py-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="project-modal-title"
          onClick={() => setSelectedItem(null)}
        >
          <div
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mx-6 mt-6 aspect-[16/9] max-h-64 bg-slate-100">
              <ProjectThumbnail item={selectedItem} />
            </div>
            <div className="px-6 pb-6 pt-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 id="project-modal-title" className="text-2xl font-semibold text-gray-900">
                    {selectedItem.title}
                  </h2>
                  <p className="mt-2 text-gray-600">{selectedItem.shortDescription}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedItem(null)}
                  className="rounded-md border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>

              <ProjectDetails item={selectedItem} />
              <p className="mt-4 text-sm text-gray-600">{selectedItem.aileronRelation}</p>

              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={selectedItem.ctaUrl}
                  target={selectedItem.ctaUrl?.startsWith('http') ? '_blank' : undefined}
                  rel={selectedItem.ctaUrl?.startsWith('http') ? 'noreferrer' : undefined}
                  className="inline-flex rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
                >
                  {selectedItem.ctaLabel}
                </a>
                <button
                  type="button"
                  onClick={() => setSelectedItem(null)}
                  className="inline-flex rounded-md border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Back to projects
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AileronCta />
    </section>
  );
}
